import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import TopNav from '../components/TopNav'
import LMSPickScreen from '../components/LMSPickScreen'
import RoundHistoryPanel from '../components/RoundHistoryPanel'
import LeaguesPanel from '../components/LeaguesPanel'
import PastRoundsTab from '../components/PastRoundsTab'

function formatCountdown(ms) {
  if (ms <= 0) return 'Locked'
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

function statusBadgeClass(status) {
  if (status === 'complete') return 'badge-blue'
  if (status === 'rolled_over') return 'badge-amber'
  return 'badge-blue'
}

// The event page is two views sharing one data load:
//   - Dashboard (default, no ?round= or ?round= the live round): a hero for
//     the CURRENT round (status open_for_entry/active) plus the player's
//     action for it (join / pick / their status), with history and leagues
//     tucked into secondary tabs below.
//   - Round detail (?round=<id> for any other round): a read-only look back
//     at one specific round -- reached by clicking a row in the Past rounds
//     tab -- reusing RoundHistoryPanel for the standings/picks/board.
// Restructured per the Predicta 2.0 dashboard-focus pass; no engine/scoring
// changes, layout and navigation only.
export default function LMSEventPage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const roundIdParam = searchParams.get('round')

  const [authUser, setAuthUser] = useState(null)
  const [event, setEvent] = useState(null)
  const [allRounds, setAllRounds] = useState([])
  const [entryMap, setEntryMap] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [fee, setFee] = useState(null)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')
  const [now, setNow] = useState(Date.now())
  const [secondaryTab, setSecondaryTab] = useState(null) // null | 'past' | 'leagues'

  const [currentGw, setCurrentGw] = useState(null)
  const [survivalDetail, setSurvivalDetail] = useState(null) // { lastSurvivedGwNumber, team }
  const [eliminationDetail, setEliminationDetail] = useState(null) // { gwNumber, teamPicked, hadPick }
  const [rolloverRound, setRolloverRound] = useState(null)
  const [roundSummary, setRoundSummary] = useState(null) // { total_entries, alive_entries }
  const [winnerName, setWinnerName] = useState(null)

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(interval)
  }, [])

  // Reset the collapsed secondary tabs whenever navigation moves between the
  // dashboard and a round-detail view, so switching rounds doesn't leave a
  // stale tab open underneath a hero that's no longer there.
  useEffect(() => { setSecondaryTab(null) }, [roundIdParam])

  useEffect(() => {
    async function load() {
      setLoading(true)
      setCurrentGw(null)
      setSurvivalDetail(null)
      setEliminationDetail(null)
      setRolloverRound(null)
      setRoundSummary(null)
      setWinnerName(null)
      setFee(null)
      setError('')

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/login'); return }
      setAuthUser(user)

      const { data: eventData } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single()

      setEvent(eventData || null)
      if (!eventData) { setLoading(false); return }

      const { data: roundsData } = await supabase
        .from('lms_rounds')
        .select('*')
        .eq('event_id', eventId)
        .order('round_number', { ascending: false })

      const rounds = roundsData || []
      setAllRounds(rounds)

      let myEntryMap = new Map()
      if (rounds.length > 0) {
        const { data: entriesData } = await supabase
          .from('lms_entries')
          .select('*')
          .eq('user_id', user.id)
          .in('round_id', rounds.map(r => r.id))

        myEntryMap = new Map((entriesData || []).map(e => [e.round_id, e]))
      }
      setEntryMap(myEntryMap)

      const currentRound = rounds.find(r => r.status === 'open_for_entry' || r.status === 'active') || null
      const requestedRound = roundIdParam ? rounds.find(r => r.id === roundIdParam) : null
      const viewedRound = requestedRound || currentRound
      const isCurrentView = !requestedRound || requestedRound.id === currentRound?.id
      const viewedEntry = viewedRound ? myEntryMap.get(viewedRound.id) || null : null

      if (viewedRound) {
        // gameweeks are public-read (RLS: gameweeks_read true), safe to fetch
        // directly for header stats and elimination/survival messaging.
        const { data: gwData } = await supabase
          .from('lms_gameweeks')
          .select('*')
          .eq('round_id', viewedRound.id)
          .order('gameweek_number', { ascending: true })

        const gws = gwData || []
        const openGw = gws.find(g => g.status !== 'processed') || null
        const processedGws = gws.filter(g => g.status === 'processed')
        const lastProcessedGw = processedGws.length > 0 ? processedGws[processedGws.length - 1] : null
        setCurrentGw(openGw)

        // aggregate round headcounts: lms_entries SELECT is own-row-only by
        // RLS (entries_read_own, migration 014), so counting survivors across
        // all players requires the SECURITY DEFINER RPC (migration 016).
        const { data: summaryData, error: summaryError } = await supabase.rpc('get_lms_round_summary', { p_round_id: viewedRound.id })
        if (!summaryError && summaryData) {
          setRoundSummary(Array.isArray(summaryData) ? summaryData[0] : summaryData)
        }

        if (viewedRound.status === 'complete') {
          const { data: winnerData } = await supabase.rpc('get_lms_round_winner_name', { p_round_id: viewedRound.id })
          setWinnerName(winnerData || null)
        }

        if (viewedEntry) {
          if (viewedEntry.status === 'pending_payment') {
            const { data: feeData } = await supabase.rpc('get_lms_entry_fee', {
              p_round_id: viewedEntry.round_id,
              p_user_id: user.id
            })
            setFee(feeData ?? null)
          }

          if (viewedEntry.status === 'active' && lastProcessedGw) {
            const { data: lastPick } = await supabase
              .from('lms_picks')
              .select('*')
              .eq('entry_id', viewedEntry.id)
              .eq('gameweek_id', lastProcessedGw.id)
              .maybeSingle()
            if (lastPick?.outcome === 'survived') {
              setSurvivalDetail({ lastSurvivedGwNumber: lastProcessedGw.gameweek_number, team: lastPick.team_picked })
            }
          }

          if (viewedEntry.status === 'eliminated') {
            if (viewedRound.status === 'rolled_over') {
              const next = rounds.find(r => r.parent_round_id === viewedRound.id)
              setRolloverRound(next || null)
            } else if (viewedEntry.eliminated_gameweek_id) {
              const gw = gws.find(g => g.id === viewedEntry.eliminated_gameweek_id)
              const { data: pick } = await supabase
                .from('lms_picks')
                .select('*')
                .eq('entry_id', viewedEntry.id)
                .eq('gameweek_id', viewedEntry.eliminated_gameweek_id)
                .maybeSingle()
              setEliminationDetail({
                gwNumber: gw?.gameweek_number ?? null,
                teamPicked: pick?.team_picked ?? null,
                hadPick: !!pick
              })
            }
          }
        } else if (isCurrentView && viewedRound.status === 'open_for_entry') {
          // no entry yet: fetch the fee up front so the join copy can show
          // the real amount without an extra click.
          const { data: feeData, error: feeError } = await supabase.rpc('get_lms_entry_fee', {
            p_round_id: viewedRound.id,
            p_user_id: user.id
          })
          if (feeError) setError(feeError.message)
          setFee(feeData ?? null)
        }
      }

      setLoading(false)
    }
    load()
  }, [eventId, navigate, roundIdParam])

  const currentRound = allRounds.find(r => r.status === 'open_for_entry' || r.status === 'active') || null
  const requestedRound = roundIdParam ? allRounds.find(r => r.id === roundIdParam) : null
  const viewedRound = requestedRound || currentRound
  const isCurrentView = !requestedRound || requestedRound.id === currentRound?.id
  const viewedEntry = viewedRound ? entryMap.get(viewedRound.id) || null : null

  const pastRounds = allRounds
    .filter(r => (r.status === 'complete' || r.status === 'rolled_over') && entryMap.has(r.id))
    .map(r => ({ round: r, entry: entryMap.get(r.id) }))

  async function handleJoin() {
    if (!currentRound || !authUser) return
    setJoining(true)
    setError('')

    const { data, error: joinError } = await supabase
      .from('lms_entries')
      .insert({ round_id: currentRound.id, user_id: authUser.id })
      .select()
      .single()

    setJoining(false)

    if (joinError) { setError(joinError.message); return }
    setEntryMap(prev => new Map(prev).set(currentRound.id, data))
  }

  if (loading) return <p className="text-muted" style={{ padding: '40px', textAlign: 'center' }}>Loading...</p>

  if (!event) {
    return (
      <div>
        <TopNav active="my-events" />
        <div className="container-narrow">
          <p className="text-muted">Event not found.</p>
        </div>
      </div>
    )
  }

  const deadlineMs = currentGw?.deadline ? new Date(currentGw.deadline).getTime() : null
  const countdown = deadlineMs ? formatCountdown(deadlineMs - now) : null

  return (
    <div>
      <TopNav active="my-events" />

      <div className="container-narrow">
        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '28px', fontWeight: '700', margin: '0 0 4px' }}>
          {event.name}
        </h1>
        <p className="text-muted" style={{ marginBottom: '24px' }}>Last Man Stands · <a href="/how-to-play" style={{ color: 'var(--blue-bright)' }}>How to play</a></p>

        {!isCurrentView && (
          <>
            <a href={`/lms/${event.id}`} className="nav-link" style={{ display: 'inline-block', marginBottom: '16px', color: 'var(--blue-bright)' }}>
              ← Back to dashboard
            </a>

            {!viewedRound && (
              <div className="card"><p className="text-muted">Round not found.</p></div>
            )}

            {viewedRound && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px' }}>
                  <span style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700' }}>
                    Round {viewedRound.round_number}
                  </span>
                  <span className={statusBadgeClass(viewedRound.status)}>{viewedRound.status.replace(/_/g, ' ')}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '16px' }}>
                  <div>
                    <div className="stat-num" style={{ fontSize: '22px' }}>R{viewedRound.pot_total}</div>
                    <div className="stat-label">Pot</div>
                  </div>
                  {roundSummary && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px' }}>{roundSummary.alive_entries}<span style={{ fontSize: '14px', color: 'var(--text-3)' }}>/{roundSummary.total_entries}</span></div>
                      <div className="stat-label">Survivors</div>
                    </div>
                  )}
                  {viewedRound.rollover_count > 0 && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px' }}>{viewedRound.rollover_count}</div>
                      <div className="stat-label">Rollover{viewedRound.rollover_count > 1 ? 's' : ''}</div>
                    </div>
                  )}
                </div>
                {viewedRound.status === 'complete' && winnerName && (
                  <p style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)', fontSize: '13px', color: 'var(--text-2)' }}>
                    Round winner: <strong style={{ color: 'var(--blue-bright)' }}>{winnerName}</strong>
                  </p>
                )}
              </div>
            )}

            {viewedEntry && viewedEntry.status === 'winner' && (
              <div className="card-gold card" style={{ marginBottom: '20px' }}>
                <span className="badge-blue" style={{ marginBottom: '10px' }}>Winner</span>
                <p style={{ fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>
                  You won Round {viewedRound.round_number}.
                </p>
                <p style={{ fontSize: '14px', color: 'var(--text-2)' }}>
                  You're the last one standing and you've won the pot of R{viewedRound.pot_total}. Dev will be in touch to arrange your payout.
                </p>
              </div>
            )}

            {viewedEntry && viewedEntry.status === 'eliminated' && viewedRound.status === 'rolled_over' && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <span className="badge-amber" style={{ marginBottom: '10px' }}>Rolled over</span>
                <p style={{ fontSize: '14px', marginBottom: '8px' }}>
                  Round {viewedRound.round_number} ended with no survivors — every remaining player was eliminated in the same gameweek. The pot of R{viewedRound.pot_total} rolled over{rolloverRound ? ` into Round ${rolloverRound.round_number}` : ''}.
                </p>
              </div>
            )}

            {viewedEntry && viewedEntry.status === 'eliminated' && viewedRound.status !== 'rolled_over' && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <span className="badge-red" style={{ marginBottom: '10px' }}>Eliminated</span>
                {eliminationDetail ? (
                  <p style={{ fontSize: '14px', color: 'var(--text-2)' }}>
                    {eliminationDetail.hadPick
                      ? `You were eliminated in Gameweek ${eliminationDetail.gwNumber} — your pick, ${eliminationDetail.teamPicked}, didn't get the win.`
                      : `You were eliminated in Gameweek ${eliminationDetail.gwNumber} — you had no eligible team left to pick.`}
                  </p>
                ) : (
                  <p className="text-muted" style={{ fontSize: '13px' }}>You're out of this round.</p>
                )}
              </div>
            )}

            {viewedEntry && viewedEntry.status === 'removed' && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <span className="badge-gray" style={{ marginBottom: '10px' }}>Removed</span>
                <p className="text-muted" style={{ fontSize: '13px' }}>You were removed from this round.</p>
              </div>
            )}

            {viewedRound && viewedEntry && (
              <RoundHistoryPanel entry={viewedEntry} round={viewedRound} />
            )}

            {viewedRound && !viewedEntry && (
              <div className="card"><p className="text-muted">You don't have an entry in this round.</p></div>
            )}
          </>
        )}

        {isCurrentView && (
          <>
            {!currentRound && (
              <div className="card">
                <p className="text-muted">No round is currently open for this event. Check back soon.</p>
              </div>
            )}

            {currentRound && (
              <div className="card-gold card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px' }}>
                  <span style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700' }}>
                    Round {currentRound.round_number}
                  </span>
                  <span className="badge-blue">{currentRound.status.replace(/_/g, ' ')}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '16px' }}>
                  <div>
                    <div className="stat-num" style={{ fontSize: '22px' }}>R{currentRound.pot_total}</div>
                    <div className="stat-label">Pot</div>
                  </div>
                  {roundSummary && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px' }}>{roundSummary.alive_entries}<span style={{ fontSize: '14px', color: 'var(--text-3)' }}>/{roundSummary.total_entries}</span></div>
                      <div className="stat-label">Survivors</div>
                    </div>
                  )}
                  {currentGw && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px' }}>{currentGw.gameweek_number}</div>
                      <div className="stat-label">Gameweek</div>
                    </div>
                  )}
                  {countdown && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px', color: (deadlineMs - now) < 60 * 60 * 1000 ? 'var(--red)' : undefined }}>{countdown}</div>
                      <div className="stat-label">Deadline</div>
                    </div>
                  )}
                  {currentRound.rollover_count > 0 && (
                    <div>
                      <div className="stat-num" style={{ fontSize: '22px' }}>{currentRound.rollover_count}</div>
                      <div className="stat-label">Rollover{currentRound.rollover_count > 1 ? 's' : ''}</div>
                    </div>
                  )}
                </div>

                {/* Contextual CTA -- what THIS player should do next about the
                    current round, right inside the hero card. */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--blue-border)' }}>
                  {!viewedEntry && currentRound.status === 'open_for_entry' && (
                    <>
                      <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '10px' }}>
                        {fee !== null ? `R${fee}` : 'Your entry fee'} into the prize pool. Last player standing wins the pot.
                      </p>
                      <button className="btn btn-primary" disabled={joining || fee === null} onClick={handleJoin}>
                        {joining ? 'Joining...' : fee !== null ? `Join Round ${currentRound.round_number} — R${fee}` : error ? 'Unable to load entry fee' : 'Loading entry fee...'}
                      </button>
                    </>
                  )}

                  {!viewedEntry && currentRound.status !== 'open_for_entry' && (
                    <p className="text-muted" style={{ fontSize: '13px' }}>This round is no longer open for entry.</p>
                  )}

                  {viewedEntry && viewedEntry.status === 'pending_payment' && (
                    <>
                      <span className="badge-amber" style={{ marginBottom: '8px' }}>Payment pending</span>
                      <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                        Use your full name as your payment reference. You can still <a href="#pick-section" style={{ color: 'var(--blue-bright)' }}>make your pick</a> while your payment is being confirmed.
                      </p>
                    </>
                  )}

                  {viewedEntry && viewedEntry.status === 'active' && (
                    <>
                      <span className="badge-green" style={{ marginBottom: '8px' }}>Alive</span>
                      <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                        {survivalDetail && `You survived Gameweek ${survivalDetail.lastSurvivedGwNumber} with ${survivalDetail.team}. `}
                        {currentGw ? <>Through to Gameweek {currentGw.gameweek_number} — <a href="#pick-section" style={{ color: 'var(--blue-bright)' }}>make your pick</a>.</> : 'Waiting on the next gameweek.'}
                      </p>
                    </>
                  )}

                  {viewedEntry && viewedEntry.status === 'eliminated' && (
                    <>
                      <span className="badge-red" style={{ marginBottom: '8px' }}>Eliminated</span>
                      <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                        {eliminationDetail
                          ? `Out in Gameweek ${eliminationDetail.gwNumber}. `
                          : "You're out of this round. "}
                        <a href="#history-section" style={{ color: 'var(--blue-bright)' }}>View your journey</a>.
                      </p>
                    </>
                  )}

                  {viewedEntry && viewedEntry.status === 'removed' && (
                    <>
                      <span className="badge-gray" style={{ marginBottom: '8px' }}>Removed</span>
                      <p className="text-muted" style={{ fontSize: '13px' }}>You were removed from this round.</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {currentRound && viewedEntry && viewedEntry.status === 'pending_payment' && (
              <div id="pick-section" className="card" style={{ marginBottom: '20px' }}>
                <p style={{ margin: '0 0 12px' }}>
                  Entry fee: <strong style={{ color: 'var(--blue-bright)' }}>R{fee ?? '...'}</strong>
                </p>
                <p style={{ marginBottom: '8px' }}>
                  Use your full name (name and surname) as your payment reference.
                </p>
                <p className="text-muted" style={{ fontSize: '13px' }}>
                  You'll pay by manual EFT — Dev will send you the banking details directly. Your entry activates once he confirms payment, but you can make picks in the meantime.
                </p>
              </div>
            )}

            {currentRound && viewedEntry && (viewedEntry.status === 'active' || viewedEntry.status === 'pending_payment') && (
              <div id={viewedEntry.status === 'active' ? 'pick-section' : undefined}>
                <LMSPickScreen entry={viewedEntry} round={currentRound} authUser={authUser} />
              </div>
            )}

            {error && (
              <p style={{ color: 'var(--red)', fontSize: '13px', marginTop: '12px' }}>{error}</p>
            )}

            {currentRound && viewedEntry && (
              <div id="history-section">
                <RoundHistoryPanel entry={viewedEntry} round={currentRound} />
              </div>
            )}

            <div className="tab-bar" style={{ marginTop: '28px' }}>
              <button
                className={`tab-btn ${secondaryTab === 'past' ? 'active' : ''}`}
                onClick={() => setSecondaryTab(prev => prev === 'past' ? null : 'past')}
              >
                Past rounds
              </button>
              <button
                className={`tab-btn ${secondaryTab === 'leagues' ? 'active' : ''}`}
                onClick={() => setSecondaryTab(prev => prev === 'leagues' ? null : 'leagues')}
              >
                Leagues
              </button>
            </div>

            {secondaryTab === 'past' && (
              <PastRoundsTab eventId={event.id} pastRounds={pastRounds} />
            )}

            {secondaryTab === 'leagues' && (
              <LeaguesPanel eventId={event.id} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
